import React, { useState, useEffect, memo } from 'react';
import cx from 'classnames';
import LinkHover from '../../../Buttons/Link/LinkHover';
import Button from '../../../Buttons/Button';
import Dropdown from '../../../Buttons/Dropdown/Dropdown';
import TourButtonsDrawer from './TourButtonsDrawer/TourButtonsDrawer';
import Icon from '../../../Icon/Icons';
import { useTranslation } from 'react-i18next';
import { customGaEvent, PublicGaEvents } from '../../../../utils/ga';
import ButtonHoverRender from '../../../Buttons/ButtonHover/ButtonHoverRender';
import ButtonHover from '../../../Buttons/ButtonHover/ButtonHover';
import ShareModal from '../../../Modal/ShareModal/ShareModal';
import FormModal from '../../../Modal/FormModal/FormModal';
import './TourButtons.scss';

const TourButtons = memo(({
  buttons,
  buttonStyles,
  isMobile,
  tourId,
  tourName,
  aid,
  currentTour,
}) => {
  const currentForm = currentTour?.forms && Object.keys(currentTour.forms).length > 0 ? Object.values(currentTour.forms)[0] : null;

  const [showPanel, setShowPanel] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    const checkSmallScreen = () => {
      setIsSmallScreen(window.innerWidth <= 325);
    };

    checkSmallScreen();
    window.addEventListener('resize', checkSmallScreen);
    return () => window.removeEventListener('resize', checkSmallScreen);
  }, []);

  const { t } = useTranslation();
  const { normal = null, hover = null } = buttonStyles || {};

  const showIcon = isMobile && (buttons.length > 1 || isSmallScreen);
  const toggleIconDisplay = showIcon && !showPanel ? (
    <Icon>more_horiz</Icon>
  ) : (
    <Icon>cancel_icon</Icon>
  );
  const buttonsExist = Boolean(buttons.length);
  const renderPseudoElement = buttonsExist && (
    <div className="render-pseudo-before">|</div>
  );

  const togglePanel = () => {
    setShowPanel(!showPanel);
    const panelState = showPanel ? 'Open' : 'Close';
    customGaEvent({
      action: PublicGaEvents.MOBILE_BUTTON_LIST,
      label: panelState,
    });
  };

  const toggleShareModal = () => {
    setShowShareModal(!showShareModal);
  };

  const toggleFormModal = () => {
    setShowFormModal(!showFormModal);
  };

  const renderAdditionalButtons = () => {
    return buttons.map(btn => {
      if (!btn.title) return null;
      return btn.type === 'standard' ? (
        <LinkHover
          tourId={tourId}
          tourName={tourName}
          key={btn.id}
          className="tour-btn"
          normal={normal}
          hover={hover}
          aid={aid}
          {...btn}
        />
      ) : (
        <Dropdown
          tourId={tourId}
          aid={aid}
          tourName={tourName}
          key={btn.id}
          className="tour-btn dropdown"
          normal={normal}
          hover={hover}
          isMobile={isMobile}
          {...btn}
        />
      );
    });
  };

  const renderTourButtons = () => {
    if (!isSmallScreen) {
      return null;
    }

    return (
      <React.Fragment>
        <ButtonHoverRender
          render={({ onEnter, onLeave, hovering }) => (
            <ButtonHover
              onEnter={onEnter}
              onLeave={onLeave}
              hovering={hovering}
              handleClick={toggleShareModal}
              hover={hover}
              normal={normal}
              active={showShareModal}
              isMobile={isMobile}
              className="tour-drawer-share-btn"
            >
              {t('publicTour.ada.shareLabel')}
            </ButtonHover>
          )}
        />
        {currentForm && currentForm.formDisplayed && (
          <ButtonHoverRender
            render={({ onEnter, onLeave, hovering }) => (
              <ButtonHover
                onEnter={onEnter}
                onLeave={onLeave}
                hovering={hovering}
                handleClick={toggleFormModal}
                hover={hover}
                normal={normal}
                active={showFormModal}
                isMobile={isMobile}
                className="tour-drawer-form-btn"
              >
                Form
              </ButtonHover>
            )}
          />
        )}
      </React.Fragment>
    )
  };

  const renderAllButtons = () => {
    return (
      <div className="tour-btns-wrapper">
        {renderTourButtons()}
        {renderAdditionalButtons()}
      </div>
    )
  }

  const classes = cx({
    'tour-btns-wrapper': buttonsExist,
    'single-mobile': isMobile && buttons.length === 1 && !isSmallScreen,
    'desktop-single': !isMobile && buttons.length === 1,
  });

  if (isMobile) {
    return (
      <section className="tour-links__drawer">
        {showIcon && (
          <Button
            className="panel-btn"
            handleClick={togglePanel}
            aria-expanded={showPanel}
            aria-controls="tour-btns-drawer"
            aria-label={t('publicTour.ada.linksDrawer.toggleDrawer')}
          >
            {toggleIconDisplay}
          </Button>
        )}
        {showShareModal && <ShareModal
          isOpen={showShareModal}
          closeModal={toggleShareModal}
          tourId={tourId}
          tourName={tourName}
          uptoDate
          published
        />}
        {showFormModal && currentForm && (
          <FormModal
            currentTour={currentTour}
            currentForm={currentForm}
            openModal={toggleFormModal}
            isOpen={showFormModal}
            closeModal={toggleFormModal}
            shouldShowForm={showFormModal}
            publicTour
          />
        )}
        {buttons.length > 1 || isSmallScreen ? (
          <TourButtonsDrawer
            show={showPanel}
            buttons={renderAllButtons()}
            close={togglePanel}
            normal={normal}
          />
        ) : (
          <div className={classes}>{renderAdditionalButtons()}</div>
        )}
      </section>
    );
  }

  return (
    <div className={classes}>
      {renderPseudoElement}
      {renderAdditionalButtons()}
    </div>
  );
});

export default TourButtons;